import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Link,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import IndexHeader from "app/shared-components/IndexHeader";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import DescriptionIcon from "@mui/icons-material/Description";
import { dashboardService } from "src/app/services/api/dashboard.service";
import { HostProgramData } from "src/app/@types/interfaces/host.dashboard";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#202441",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}));

export default function HostDashboard() {
  const [programs, setPrograms] = useState<HostProgramData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mainProgram, setMainProgram] = useState<string>("");
  const [datesProgram, setDatesProgram] = useState<string>("");
  const [docsProgram, setDocsProgram] = useState<string>("");
  const [statusProgram, setStatusProgram] = useState<string>("");
  const [hoursProgram, setHoursProgram] = useState<string>("");

  const programTabs = [
    "Summer Youth Employment Program",
    "School Year Program",
    "Global Education Program",
  ];

  const programOptions = [
    "Summer Youth Employment Program 2025",
    "In-School Youth Programs",
    "Youth Leadership Program",
    "Pathways for Young Adults Program",
  ];

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const resp = await dashboardService.getHostDashboardData();
        if (!resp.success || !resp.data?.programs) {
          throw new Error(resp.message || "Failed to load");
        }
        const progs = resp.data.programs;
        setPrograms(progs);
        const first = progs[0].name;
        setMainProgram(first);
        setDatesProgram(first);
        setDocsProgram(first);
        setStatusProgram(first);
        setHoursProgram(first);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getProgram = (name: string): HostProgramData | undefined =>
    programs.find((p) => p.name === name) || programs[0];

  const handleTabChange = (_: any, value: string) => {
    if (!value) return;
    setMainProgram(value);
    const matchingProgram =
      programOptions.find((p) => p.includes(value)) ?? programOptions[0];
    setDatesProgram(matchingProgram);
    setDocsProgram(matchingProgram);
    setStatusProgram(matchingProgram);
    setHoursProgram(matchingProgram);
  };

  const handleSelect =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: SelectChangeEvent) => {
      setter(e.target.value);
    };

  // chart builders
  const buildStatusChart = (progName: string) => {
    const prog = getProgram(progName);
    if (!prog) return { series: [], options: {} as ApexOptions };

    const openings =
      prog.applicationStatus.find((s) => s.status === "Openings")?.count ?? 0;
    const interviews =
      prog.applicationStatus.find((s) => s.status === "Interview Scheduled")
        ?.count ?? 0;
    const selected =
      prog.applicationStatus.find((s) => s.status === "Selected")?.count ?? 0;

    return {
      series: [openings, interviews, selected],
      options: {
        chart: {
          id: `status-chart-${progName}`,
          type: "pie" as const,
        },
        labels: ["Openings", "Interview Scheduled", "Selected"],
        colors: ["#FF9800", "#2196F3", "#4CAF50"],
        legend: { position: "bottom" as const },
        dataLabels: {
          enabled: true,
          formatter: function (val: number, opts: any) {
            return opts.w.globals.series[opts.seriesIndex].toString();
          },
        },
        tooltip: { y: { formatter: (val) => `${val}` } },
        responsive: [{ breakpoint: 480, options: { chart: { width: 300 } } }],
      } as ApexOptions,
    };
  };

  const statusChart = useMemo(() => {
    return buildStatusChart(statusProgram);
  }, [statusProgram, programs]);

  const buildHoursChart = (progName: string) => {
    const prog = getProgram(progName);
    if (!prog) return { series: [], options: {} as ApexOptions };
    return {
      series: [{ name: "Hours", data: prog.hoursWorked.map((h) => h.hours) }],
      options: {
        chart: { type: "bar" as const },
        plotOptions: { bar: { horizontal: false, columnWidth: "50%" } },
        xaxis: {
          categories: prog.hoursWorked.map((h) => h.host),
          labels: { rotate: -45 },
        },
        yaxis: { title: { text: "Hours" } },
        colors: ["#4CAF50"],
      } as ApexOptions,
    };
  };

  if (loading)
    return (
      <>
        <IndexHeader
          name="Host Dashboard"
          update={false}
          add={false}
          redirectingPath="/host-dashboard"
        />
        <Box m={4} textAlign="center">
          <CircularProgress />
        </Box>
      </>
    );

  if (error)
    return (
      <>
        <IndexHeader
          name="Host Dashboard"
          update={false}
          add={false}
          redirectingPath="/host-dashboard"
        />
        <Box m={4} textAlign="center">
          <Typography color="error">{error}</Typography>
        </Box>
      </>
    );

  return (
    <>
      <IndexHeader
        name="Host Dashboard"
        update={false}
        add={false}
        redirectingPath="/host-dashboard"
      />

      {/* Upcoming Programs */}
      <Box m={3}>
        <Typography variant="h5" gutterBottom>
          Upcoming Programs
        </Typography>
        <Box display="flex" flexWrap="wrap" sx={{ gap: 1 }}>
          {programTabs.map((prog) => (
            <Button
              key={prog}
              variant={mainProgram === prog ? "contained" : "outlined"}
              onClick={() => handleTabChange(null, prog)}
              sx={{
                textTransform: "none",
                fontWeight: mainProgram === prog ? 600 : 400,
                px: 3,
                py: 1,
              }}
            >
              {prog}
            </Button>
          ))}
        </Box>
      </Box>

      <Box m={3}>
        {/* Charts */}
        <Typography variant="h5" gutterBottom>
          Program Analytics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="h6">Application Status</Typography>
                  <FormControl size="small">
                    <InputLabel>Program</InputLabel>
                    <Select
                      value={statusProgram}
                      label="Program"
                      onChange={handleSelect(setStatusProgram)}
                    >
                      {programOptions.map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Program: {statusProgram}
                </Typography>
                <Chart
                  key={`status-chart-${statusProgram}`}
                  options={statusChart.options}
                  series={statusChart.series}
                  type="pie"
                  height={300}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="h6">Hours Worked</Typography>
                  <FormControl size="small">
                    <InputLabel>Program</InputLabel>
                    <Select
                      value={hoursProgram}
                      label="Program"
                      onChange={handleSelect(setHoursProgram)}
                    >
                      {programOptions.map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Program: {hoursProgram}
                </Typography>
                <Chart
                  options={buildHoursChart(hoursProgram).options}
                  series={buildHoursChart(hoursProgram).series}
                  type="bar"
                  height={300}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tables */}
        <Box mt={5}>
          <Typography variant="h5" gutterBottom>
            Program Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="h6">Important Dates</Typography>
                    <FormControl size="small">
                      <InputLabel>Program</InputLabel>
                      <Select
                        value={datesProgram}
                        label="Program"
                        onChange={handleSelect(setDatesProgram)}
                      >
                        {programOptions.map((p) => (
                          <MenuItem key={p} value={p}>
                            {p}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Program: {datesProgram}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Event</StyledTableCell>
                          <StyledTableCell>Date</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getProgram(datesProgram)?.importantDates.map((d) => (
                          <StyledTableRow key={d.label}>
                            <StyledTableCell>{d.label}</StyledTableCell>
                            <StyledTableCell>{d.value}</StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="h6">Important Documents</Typography>
                    <FormControl size="small">
                      <InputLabel>Program</InputLabel>
                      <Select
                        value={docsProgram}
                        label="Program"
                        onChange={handleSelect(setDocsProgram)}
                      >
                        {programOptions.map((p) => (
                          <MenuItem key={p} value={p}>
                            {p}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Program: {docsProgram}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Document</StyledTableCell>
                          <StyledTableCell>Action</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getProgram(docsProgram)?.documents.map((doc) => (
                          <StyledTableRow key={doc.label}>
                            <StyledTableCell>
                              <Box display="flex" alignItems="center">
                                <DescriptionIcon sx={{ mr: 1, fontSize: 16 }} />
                                {doc.label}
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell>
                              <Link href={doc.url} underline="none">
                                <Button size="small" variant="outlined">
                                  View
                                </Button>
                              </Link>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}
